// React hooks for state management
import { useState } from 'react';
// Axios library for making HTTP requests
import axios from 'axios';
// React Router hooks for reading URL parameters and programmatic navigation
import { useParams, useNavigate } from 'react-router-dom';
// Custom authentication hook to get user info and loading state
import { useAuth } from '../hooks/useAuth';

function CreateDeadlinePage() {
  // Destructure user and loading state from authentication hook
  const { user, isLoading } = useAuth();
  // Get the course ID from the URL parameters
  const { course_id } = useParams<{ course_id: string }>();
  // Navigation hook for redirecting the user
  const navigate = useNavigate();
  // Local state to store form inputs for title, description, and due date
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    due_date: '',
  });
  // Local state for any error messages
  const [error, setError] = useState<string | null>(null);
  // Local state to track submission status
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Display loading spinner if authentication is in progress or user is not loaded
  if (isLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-600"></div>
      </div>
    );
  }
  // Redirect non-teacher users to login page
  if (user.role !== 'teacher') {
    navigate('/login');
    return null;
  }
  // Handle changes to form inputs (title, description, due date)
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); // Prevent default form submission
    setIsSubmitting(true); // Set submitting state to true

    try {
      // Fetch CSRF token for secure POST request
      const response = await axios.get('/elearning/get-csrf/', { withCredentials: true });
      const csrfToken = response.data.csrfToken;

      // Submit the deadline to the backend API
      await axios.post(
        '/elearning/api/deadlines/',
        {
          course_id: course_id,
          title: formData.title,
          description: formData.description,
          due_date: formData.due_date,
        },
        {
          withCredentials: true,
          headers: { 'X-CSRFToken': csrfToken, 'Content-Type': 'application/json' },
        }
      );
      setError(null); // Clear any previous errors
      navigate(`/courses/${course_id}`); // Navigate back to the course page
    } catch (err: any) {
      // Handle errors and display to the user
      const errorMessage = err.response?.data?.message || err.message || 'Failed to create deadline';
      console.error('CreateDeadlinePage: Error=', errorMessage);
      setError(errorMessage);
    } finally {
      setIsSubmitting(false); // Reset submitting state
    }
  };
  // -----------------------------
// Create Deadline Form
// -----------------------------
return (
  <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
    <div className="w-full max-w-2xl bg-white rounded-2xl shadow-lg p-8 border border-gray-200">

      {/* Page heading */}
      <h1 className="text-3xl font-extrabold text-gray-900 mb-8 tracking-tight">
        📝 Create Deadline
      </h1>

      {/* Error message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 border border-red-200 rounded-lg shadow-sm">
          {error}
        </div>
      )}

      {/* Deadline creation form */}
      <form onSubmit={handleSubmit} className="space-y-6">

        {/* Title input */}
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700">
            Title
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="mt-2 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm p-3"
            required
          />
        </div>

        {/* Description input */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700">
            Description
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={4}
            className="mt-2 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm p-3"
            required
          />
        </div>

        {/* Due date input */}
        <div>
          <label htmlFor="due_date" className="block text-sm font-medium text-gray-700">
            Due Date
          </label>
          <input
            type="datetime-local"
            id="due_date"
            name="due_date"
            value={formData.due_date}
            onChange={handleChange}
            className="mt-2 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm p-3"
            required
          />
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-between">
          <a
            href={`/courses/${course_id}`}
            className="px-5 py-2 bg-gray-200 text-gray-900 rounded-lg shadow hover:bg-gray-300 transition"
          >
            Back to Course
          </a>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`px-6 py-2 bg-blue-600 text-white font-medium rounded-lg shadow hover:bg-blue-700 transition ${
              isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isSubmitting ? 'Creating...' : 'Create Deadline'}
          </button>
        </div>
      </form>
    </div>
  </div>
);

}
// Export the component for use in the app
export default CreateDeadlinePage;
